import type {
  CompanySkipReason,
  ResolvedCompanies,
  SkippedCompany,
} from "@/lib/resolved-companies";
import {
  companyResolutionKey,
  resolveCompany,
  type CompanyResolutionParams,
} from "@/lib/company-resolution";
import { toCompanyTag } from "@/lib/company-tag";
import { postCompaniesFromTags } from "@/lib/company-tag-creation";
import {
  COMPANY_FROM_TAGS_BATCH_MAX,
  companyPreviewLookupKey,
  normalizeCompanyName,
  normalizeText,
  type CompanyTag,
} from "@shaarp/common";
import type { AxiosInstance } from "axios";

// A chip opens on its tag and looks nothing up, so adding a table to a list has
// to resolve every row. Cap the fan-out so a wide table doesn't fire dozens of
// simultaneous lookups. Rows saved from their tag go by batch to the server,
// which saves a batch four rows at a time — a row may run a level-2 enrichment
// of several seconds.
const MAX_CONCURRENT_RESOLUTIONS = 4;

/**
 * A name with a letter or a digit: what `POST /companies/from-tags` requires
 * of every tag (`parseCompanyTagRequest`), refusing the whole batch otherwise.
 */
const NAMES_SOMETHING = /[\p{L}\p{N}]/u;

/** One row of the table to place: by its identifier, or by its tag. */
export type RowToSave =
  | { readonly kind: "identified"; readonly params: CompanyResolutionParams }
  | { readonly kind: "unidentified"; readonly tag: CompanyTag };

/** Where one row landed: a company, or why none. */
type RowOutcome =
  | { readonly companyId: string }
  | { readonly reason: CompanySkipReason };

/**
 * Where an identified row lands: resolved by its identifier, through the
 * session-wide resolution cache, or — when the identifier finds nothing — the
 * tag it is to be saved from. Ambiguous identifiers (several homonyms in the
 * team's database) are not guessed at. A request that failed is
 * `unavailable`: worth one more try.
 */
async function identifiedOutcome(
  api: AxiosInstance,
  params: CompanyResolutionParams,
): Promise<RowOutcome | { readonly tag: CompanyTag }> {
  try {
    const key = companyResolutionKey(params);
    if (key) {
      const resolution = await resolveCompany(api, key, params);
      if (resolution.status === "company") {
        return { companyId: resolution.company.id };
      }
      if (resolution.status === "candidates") return { reason: "homonyms" };
    }
    const tag = toCompanyTag(params);
    return tag ? { tag } : { reason: "not_identified" };
  } catch {
    return { reason: "unavailable" };
  }
}

/**
 * What each tag is saved as, in batches of `COMPANY_FROM_TAGS_BATCH_MAX`, one
 * after the other — `POST /companies/from-tags` saves a batch four at a time,
 * and records one history row for it. A batch whose request failed is
 * `unavailable` row by row.
 */
async function saveTags(
  api: AxiosInstance,
  tags: readonly CompanyTag[],
  onOutcome: (index: number, outcome: RowOutcome) => void,
): Promise<void> {
  for (let start = 0; start < tags.length; start += COMPANY_FROM_TAGS_BATCH_MAX) {
    const batch = tags.slice(start, start + COMPANY_FROM_TAGS_BATCH_MAX);
    let results: Awaited<ReturnType<typeof postCompaniesFromTags>> | null = null;
    try {
      results = await postCompaniesFromTags(api, batch);
    } catch {
      // Reported below, row by row, as worth another try.
    }
    batch.forEach((_tag, offset) => {
      const result = results?.[offset];
      onOutcome(
        start + offset,
        !result
          ? { reason: "unavailable" }
          : result.status === "company"
            ? { companyId: result.companyId }
            : { reason: result.reason },
      );
    });
  }
}

const nameOf = (row: RowToSave): string | undefined =>
  row.kind === "identified" ? row.params.name : row.tag.name;

/**
 * Every identifier of an identified row. `companyResolutionKey` keeps one of
 * them — the city before the SIREN — so "Martin SARL, Lyon, SIREN 1…" and
 * "Martin SAS, Lyon, SIREN 2…" would share it: the others tell them apart.
 */
const identifiersOf = ({
  id,
  siret,
  siren,
  domain,
  linkedin,
}: CompanyResolutionParams): string =>
  [id, siret, siren, domain, linkedin]
    .map((identifier) => identifier?.trim().toLowerCase() ?? "")
    .join("|");

/**
 * The identity of a row: two rows with one key are one company, looked up
 * once, and a table whose rows changed never reuses a lookup. The name is
 * read without its legal forms and the city without its case and accents, so
 * "Acme SAS, Lyon" and "Établissements ACME, lyon" are one row — unless an
 * identifier differs. Empty when the row names nothing to look up.
 */
export function tableRowKey(row: RowToSave): string {
  if (row.kind === "unidentified") {
    return companyPreviewLookupKey(row.tag) ?? row.tag.name;
  }
  const key = companyResolutionKey({
    ...row.params,
    name: normalizeCompanyName(row.params.name),
    city: normalizeText(row.params.city),
  });
  return key ? `${key}|${identifiersOf(row.params)}` : "";
}

/**
 * The rows once each, in reading order: a company the table cites twice under
 * two spellings is sent once, not raced against itself. A row with no key
 * names nothing to look up and is kept, to be counted as left out.
 */
function distinctRows(rows: readonly RowToSave[]): RowToSave[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = tableRowKey(row);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Every row of a chat table to a company id, the rows left out named with why
 * — what "Save to a list" attaches. Rows sharing a `tableRowKey` are sent
 * once. `onResolved` hears each id as soon as its row is placed, so the list
 * can fill while the slower rows are still looked up.
 *
 * Identified rows are resolved by their identifier while the unidentified
 * ones are saved from their tag, batch by batch; an identified row its
 * identifier does not find is saved from its tag once they are done.
 *
 * A row that came back `unavailable` — the server busy with the same company,
 * a save past its time, a request that failed — is tried once more once every
 * other row has had its turn; a row the day's budget or homonyms kept out is
 * not, since asking again now would answer the same.
 */
export async function resolveTableRows(
  api: AxiosInstance,
  tableRows: readonly RowToSave[],
  onResolved?: (companyId: string) => void,
): Promise<ResolvedCompanies> {
  const companiesIds: string[] = [];

  /** Places `rows`; answers those left out, and why. */
  const place = async (
    rows: readonly RowToSave[],
  ): Promise<{ row: RowToSave; reason: CompanySkipReason }[]> => {
    const leftOut: { row: RowToSave; reason: CompanySkipReason }[] = [];
    const land = (row: RowToSave, outcome: RowOutcome): void => {
      if ("companyId" in outcome) {
        companiesIds.push(outcome.companyId);
        onResolved?.(outcome.companyId);
      } else {
        leftOut.push({ row, reason: outcome.reason });
      }
    };
    const saveFromTags = (
      tagged: readonly { row: RowToSave; tag: CompanyTag }[],
    ): Promise<void> => {
      // The server refuses a whole batch over one tag whose name has no
      // letter or digit: such a row is left out here, the others are sent.
      const sendable = tagged.filter(({ row, tag }) => {
        if (NAMES_SOMETHING.test(tag.name)) return true;
        land(row, { reason: "not_identified" });
        return false;
      });
      return saveTags(
        api,
        sendable.map(({ tag }) => tag),
        (index, outcome) => land(sendable[index].row, outcome),
      );
    };

    const identified = rows.filter(
      (row): row is Extract<RowToSave, { kind: "identified" }> =>
        row.kind === "identified",
    );
    const fallbacks: { row: RowToSave; tag: CompanyTag }[] = [];
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < identified.length) {
        const row = identified[cursor++];
        const outcome = await identifiedOutcome(api, row.params);
        if ("tag" in outcome) fallbacks.push({ row, tag: outcome.tag });
        else land(row, outcome);
      }
    };

    await Promise.all([
      saveFromTags(
        rows.flatMap((row) =>
          row.kind === "unidentified" ? [{ row, tag: row.tag }] : [],
        ),
      ),
      ...Array.from(
        { length: Math.min(MAX_CONCURRENT_RESOLUTIONS, identified.length) },
        worker,
      ),
    ]);
    await saveFromTags(fallbacks);
    return leftOut;
  };

  const firstPass = await place(distinctRows(tableRows));
  const retried = await place(
    firstPass
      .filter(({ reason }) => reason === "unavailable")
      .map(({ row }) => row),
  );
  const leftOut = [
    ...firstPass.filter(({ reason }) => reason !== "unavailable"),
    ...retried,
  ];

  // Two identities that turn out to be one company collapse to one id.
  return {
    companiesIds: [...new Set(companiesIds)],
    skipped: leftOut.map(({ row, reason }): SkippedCompany => {
      const name = nameOf(row);
      return name ? { name, reason } : { reason };
    }),
  };
}
